import { useEffect, useContext, useState } from "react";
import {
  Modal,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ToastContext } from "../../providers/ToastProvider";
import { DrawerContext } from "../../providers/DrawerProvider";
import { useForm } from "react-hook-form";
import { useTheme } from "@mui/material/styles";
import { validateField } from "../../common/globalFunctions";

const StockAdder = ({ 
  open, 
  handleClose, 
  currentItem, 
  stockService, 
  setStock, 
  setItemsLoading, 
  itemsLoading 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const { drawerOpen } = useContext(DrawerContext);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      quantity: "",
    },
  });
  const theme = useTheme();

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const addQuantity = Number(data.quantity);
      
      if (addQuantity <= 0) {
        setToastMessage([
          {
            severity: "error",
            primary: "La cantidad debe ser mayor a 0",
          },
        ]);
        setShowToast(true);
        return;
      }

      const newQuantity = Number(currentItem.quantity) + addQuantity;

      setItemsLoading((oldItems) => [
        ...oldItems,
        {
          id: currentItem.id,
          edit: true,
        },
      ]);

      const payload = [
        {
          stockId: currentItem.id,
          quantity: newQuantity,
        },
      ];

      const res = await stockService.update(payload);

      if (!res.data.success) {
        return;
      }

      setStock((oldStock) => {
        const idx = oldStock.findIndex((aStock) => aStock.id === currentItem.id);
        oldStock[idx].quantity = newQuantity;
        return [...oldStock];
      });

      setToastMessage([
        {
          severity: "success",
          primary: `Stock actualizado correctamente`,
        },
      ]);
      setShowToast(true);
      handleClose();
    } catch (error) {
      setToastMessage([
        {
          severity: "error",
          primary: "Error al intentar agregar stock",
        },
      ]);
      setShowToast(true);
    } finally {
      setItemsLoading((oldItems) => [
        ...oldItems.filter((item) => item.id !== currentItem.id),
      ]);
      setIsLoading(false);
    }
  };

  const keyPress = (e) => {
    if (e.keyCode === 13) {
      handleSubmit(onSubmit)();
    }
  };

  useEffect(() => {
    reset();
  }, [reset, open]);

  const classes = {
    root: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      left: drawerOpen ? theme.drawerWidth : 0,

      "& .MuiModal-backdrop": {
        left: drawerOpen ? theme.drawerWidth : 0,
      },
    },
    paper: {
      width: "90%",
      maxWidth: 400,
      textAlign: "center",
      position: "relative",
      top: -50,
      pt: 3,
      pb: 5,
      px: 6,
    },
    actions: {
      display: "flex",
      justifyContent: "center",
      gap: 2,
    },
  };

  return (
    <Modal open={open} onClose={handleClose} sx={classes.root}>
      <Paper onKeyDown={keyPress} sx={classes.paper}>
        <Box
          component="form"
          autoComplete="off"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography variant="h6" component="h3" mb={3}>
            <Box component={FontAwesomeIcon} icon="plus" mr={2} />
            Agregar Stock
          </Typography>
          
          {currentItem && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {currentItem.size} - {currentItem.color}
              <br />
              Stock actual: {currentItem.quantity}
            </Typography>
          )}

          <TextField
            label="Cantidad a agregar"
            variant="filled"
            type="number"
            autoFocus
            autoComplete="off"
            disabled={isLoading}
            sx={{ width: "100%", mb: 3 }}
            {...register("quantity", {
              required: true,
              pattern: /^[1-9]\d*$/,
              min: 1,
            })}
            error={errors.quantity !== undefined}
            helperText={validateField(errors.quantity)}
          />

          <Box sx={classes.actions}>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ width: isLoading ? "100%" : "fit-content" }}
            >
              {isLoading ? (
                <>
                  Agregando stock{" "}
                  <CircularProgress
                    sx={{ ml: 1 }}
                    thickness={3}
                    size={20}
                    color="secondary"
                  />
                </>
              ) : (
                "Aceptar"
              )}
            </Button>
            {!isLoading && (
              <Button variant="contained" color="error" onClick={handleClose}>
                Cancelar
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default StockAdder;